import {
  BoatingRiskRating,
  WaterBody,
  calculateWindSea,
  fetchForWaterBody,
} from '@deckee/utilities';
import {
  CLOUD_TARGET,
  FRAME_TIME_EMA_ALPHA,
  HEAVE_LIMIT_FRACTION,
  HORIZON_FRACTION,
  HULL_PX_BASE,
  HULL_PX_MIN,
  HULL_PX_PER_M,
  INLAND_CHOP_FULL_MPS,
  INLAND_CHOP_START_MPS,
  MAX_FRAME_DT_S,
  PHYSICS_MAX_STEPS,
  PHYSICS_STEP_S,
  QUALITY_LEVELS,
  RAIN_TARGET,
  SKY_EASE_RATE,
  SLOW_FRAME_S,
  SLOW_FRAMES_BEFORE_STEP_DOWN,
  STORM_TARGET,
  WAVE_CLAMP_FRACTION,
} from './constants';
import { HullDynamics } from './hullDynamics';
import { FoamRenderer } from './layers/FoamRenderer';
import { SkyRenderer } from './layers/SkyRenderer';
import { WaterRenderer } from './layers/WaterRenderer';
import { WaveScaleRenderer } from './layers/WaveScaleRenderer';
import { daylightAt, smoothstep } from './math';
import { SeaSpectrum } from './seaSpectrum';
import { skyStateForWeatherCode } from './skyState';
import type {
  ForecastAnimationPerformance,
  ForecastAnimationScene,
} from './types';
import { BoatPalette, drawBoat } from './vessels';
import { calculateWhitecaps } from './whitecaps';

export interface SeaSceneOptions {
  canvas: HTMLCanvasElement;
  reducedMotion: boolean;
  onPerformanceSample?: (sample: ForecastAnimationPerformance) => void;
}

/**
 * The animation's runtime: it owns the canvas, the frame loop, the sea, the hull
 * and the four draw layers.
 *
 * React drives it through `setScene`, `resize`, `setVisible` and
 * `setTimeScale` — so the component stays a thin shell and none of the
 * animation state lives in React.
 */
export class SeaScene {
  private readonly ctx: CanvasRenderingContext2D;

  private readonly sea = new SeaSpectrum();

  private readonly hull = new HullDynamics();

  private readonly palette: BoatPalette;

  private readonly sky: SkyRenderer;

  private readonly water: WaterRenderer;

  private readonly foam: FoamRenderer;

  private readonly waveScale: WaveScaleRenderer;

  private scene: ForecastAnimationScene | null = null;

  private width = 0;

  private height = 0;

  private devicePixelRatio = 1;

  /** Quality level: 0 full detail, 2 the low-power floor. Only ever steps down. */
  private quality = 0;

  private frameHandle: number | null = null;

  private lastFrameTime = 0;

  /** Leftover time carried between frames so physics runs on a fixed sub-step. */
  private accumulator = 0;

  private averageDt = 1 / 60;

  private slowFrameCount = 0;

  private visible = true;

  /** Multiplier applied to each frame's delta. 1 = realtime; higher plays faster. */
  private timeScale = 1;

  /** Eased backdrop amounts, so sky states cross-fade as the hour changes. */
  private cloudAmount = 0;

  private rainAmount = 0;

  private stormAmount = 0;

  constructor(private readonly options: SeaSceneOptions) {
    const ctx = options.canvas.getContext('2d');

    if (ctx == null) {
      throw new Error('ForecastAnimation: 2D canvas context is unavailable');
    }

    this.ctx = ctx;
    this.palette = new BoatPalette(ctx);
    this.sky = new SkyRenderer(ctx);
    this.water = new WaterRenderer(ctx);
    this.foam = new FoamRenderer(ctx);
    this.waveScale = new WaveScaleRenderer(ctx);
  }

  private qualitySettings(): (typeof QUALITY_LEVELS)[number] {
    return QUALITY_LEVELS[this.quality];
  }

  setScene(scene: ForecastAnimationScene): void {
    const previous = this.scene;
    this.scene = scene;

    // A location switch changes what the sea even is, so transients from the old
    // one should not drift across into the new one.
    if (previous != null && previous.waterBody !== scene.waterBody) {
      this.water.reset();
      this.foam.reset();
      this.hull.chop = 0;
    }
  }

  /** Match the backing store to the CSS box, honouring the device pixel ratio cap. */
  resize(cssWidth: number, cssHeight: number): void {
    const { canvas } = this.options;

    this.devicePixelRatio = Math.min(
      window.devicePixelRatio || 1,
      this.qualitySettings().dprCap,
    );
    this.width = cssWidth;
    this.height = cssHeight;

    canvas.width = Math.round(cssWidth * this.devicePixelRatio);
    canvas.height = Math.round(cssHeight * this.devicePixelRatio);
    this.ctx.setTransform(
      this.devicePixelRatio,
      0,
      0,
      this.devicePixelRatio,
      0,
      0,
    );

    // Size-dependent gradient caches must rebuild.
    this.sky.invalidate();
    this.water.invalidate();
  }

  /** Rendering pauses entirely while the canvas is scrolled out of view. */
  setVisible(visible: boolean): void {
    this.visible = visible;
  }

  /**
   * Scale simulated time relative to wall-clock. The quality ladder still sees
   * the unscaled frame interval so a faster playback does not look like a
   * struggling device.
   */
  setTimeScale(scale: number): void {
    this.timeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
  }

  start(): void {
    if (this.frameHandle != null) {
      return;
    }

    this.lastFrameTime = performance.now();
    this.frameHandle = requestAnimationFrame(this.frame);
  }

  stop(): void {
    if (this.frameHandle != null) {
      cancelAnimationFrame(this.frameHandle);
      this.frameHandle = null;
    }
  }

  private frame = (now: number): void => {
    this.frameHandle = requestAnimationFrame(this.frame);

    if (!this.visible || this.scene == null || this.width === 0) {
      this.lastFrameTime = now;

      return;
    }

    const rawDt = (now - this.lastFrameTime) / 1000;
    const dt = Math.min(MAX_FRAME_DT_S, rawDt) * this.timeScale;
    this.lastFrameTime = now;

    this.updateQuality(rawDt);
    this.render(now, dt, this.scene);
  };

  /**
   * The adaptive quality ladder: a sustained run of slow frames steps the detail
   * down one level. It is deliberately one-way — a device that struggles settles
   * at a level rather than oscillating between two.
   */
  private updateQuality(rawDt: number): void {
    if (this.options.reducedMotion) {
      return;
    }

    this.averageDt +=
      (Math.min(rawDt, 0.1) - this.averageDt) * FRAME_TIME_EMA_ALPHA;

    if (this.averageDt > SLOW_FRAME_S) {
      this.slowFrameCount += 1;

      if (
        this.slowFrameCount > SLOW_FRAMES_BEFORE_STEP_DOWN &&
        this.quality < QUALITY_LEVELS.length - 1
      ) {
        this.quality += 1;
        this.slowFrameCount = 0;
        this.averageDt = 1 / 60;
        this.resize(this.width, this.height);
      }
    } else if (this.slowFrameCount > 0) {
      this.slowFrameCount -= 1;
    }

    this.options.onPerformanceSample?.({
      averageFrameMs: this.averageDt * 1000,
      qualityLevel: this.quality,
      devicePixelRatio: this.devicePixelRatio,
    });
  }

  private render(now: number, dt: number, scene: ForecastAnimationScene): void {
    const { ctx, width, height } = this;
    const { conditions, vessel, waterBody, rating, hour, weatherCode } = scene;
    const { reducedMotion } = this.options;

    const inland = waterBody === WaterBody.Inland;
    const inlandChop = inland
      ? smoothstep(
          INLAND_CHOP_START_MPS,
          INLAND_CHOP_FULL_MPS,
          conditions.windSpeedMps,
        )
      : 0;

    // The wind raises its own sea on top of any swell: a long fetch on the open
    // coast, a shorter one inland. Same model the rating uses, so the drawn sea
    // and the rated sea are always the same sea.
    const windSea = calculateWindSea(
      conditions.windSpeedMps,
      fetchForWaterBody(waterBody),
    );

    const swellHeightM = inland ? 0 : conditions.swellHeightM;
    const swellPeriodS = inland ? 0 : conditions.swellPeriodS;

    const whitecaps = inland
      ? calculateWhitecaps(
          conditions.windSpeedMps,
          conditions.windDirectionDeg,
          windSea.heightM,
          windSea.periodS,
          conditions.windDirectionDeg,
        )
      : calculateWhitecaps(
          conditions.windSpeedMps,
          conditions.windDirectionDeg,
          conditions.swellHeightM,
          conditions.swellPeriodS,
          conditions.swellDirectionDeg,
        );

    // The boat is the constant reference: its on-screen size is fixed by its
    // length, and the sea is then drawn at the boat's own pixels-per-metre.
    const lengthM = vessel.lengthM;
    const hullPx = Math.max(
      HULL_PX_MIN,
      HULL_PX_BASE + lengthM * HULL_PX_PER_M,
    );
    const pxPerM = hullPx / lengthM;

    const significantHeightM = Math.hypot(swellHeightM, windSea.heightM);
    const baseY = height * HORIZON_FRACTION;
    const clampPx = height * WAVE_CLAMP_FRACTION;

    const tuning = {
      centreX: width / 2,
      halfWidthPx: hullPx * 0.45,
      // Pitch period is about 1.0 × √L; heave is shorter, about 0.75 × √L.
      pitchFrequency: (2 * Math.PI) / (1.02 * Math.sqrt(lengthM)),
      heaveFrequency: (2 * Math.PI) / (0.75 * Math.sqrt(lengthM)),
      heaveLimitPx: height * HEAVE_LIMIT_FRACTION,
      inlandChop,
    };

    this.sea.build({
      swellHeightM,
      swellPeriodS,
      windSeaHeightM: windSea.heightM,
      windSeaPeriodS: windSea.periodS,
      pxPerM,
      width,
      height,
      clampPx,
      inlandChop,
    });

    // The one and only capsize trigger: the app's live rating says red.
    this.hull.capsizeTarget = rating === BoatingRiskRating.Red ? 1 : 0;

    if (reducedMotion) {
      this.hull.snapTo(this.sea, tuning);
    } else {
      this.accumulator += dt;
      let steps = 0;
      // Faster playback advances more simulated time per frame; raise the step
      // budget with the scale so the hull stays in lockstep with the sea.
      const maxSteps =
        PHYSICS_MAX_STEPS * Math.max(1, Math.ceil(this.timeScale));

      while (this.accumulator >= PHYSICS_STEP_S && steps < maxSteps) {
        this.hull.step(PHYSICS_STEP_S, this.sea, tuning);
        this.accumulator -= PHYSICS_STEP_S;
        steps += 1;
      }
    }

    /* ----- draw ----- */

    ctx.clearRect(0, 0, width, height);

    const daylight = daylightAt(hour);
    this.palette.setDaylight(daylight);

    this.easeBackdrop(weatherCode, dt);

    const windRamp = smoothstep(3, 15, conditions.windSpeedMps);
    const timeS = now / 1000;
    const effectiveJudderChop = reducedMotion
      ? 0
      : this.hull.chop * (1 - this.hull.capsize);
    const judder = this.sea.judderOffsets(effectiveJudderChop, hullPx);
    const { stepX, foamMax, spindriftMax, particleMax } =
      this.qualitySettings();

    this.sky.draw({
      width,
      height,
      baseY,
      daylight,
      cloud: this.cloudAmount,
      storm: this.stormAmount,
      windRamp,
      dt,
      timeS,
      reducedMotion,
    });

    // The surface polyline is built before the rain so drops can end at the real
    // surface, and is then reused by the water fill, foam, rings and churn.
    this.water.buildSurface(width, baseY, clampPx, stepX, (x) =>
      this.sea.elevationAt(x),
    );

    const waterFrame = {
      width,
      height,
      baseY,
      daylight,
      rain: this.rainAmount,
      cloud: this.cloudAmount,
      windRamp,
      hour,
      clampPx,
      dt,
      reducedMotion,
    };

    this.water.drawRain(waterFrame);

    // The boat is drawn before the water so the water pass seats the hull —
    // no sky showing under the waterline.
    drawBoat({
      palette: this.palette,
      graphic: vessel.graphic,
      centreX: tuning.centreX,
      baseY,
      hullPx,
      heavePx: this.hull.heavePx,
      pitchRad: this.hull.pitchRad,
      capsize: this.hull.capsize,
      judderYPx: judder.yPx,
      judderRotationRad: judder.rotationRad,
      night: 1 - daylight,
      windMps: conditions.windSpeedMps,
      reducedMotion,
      timeS,
    });

    this.water.drawWater(waterFrame);
    this.water.drawRings(waterFrame);

    const foamFrame = {
      daylight,
      dt,
      timeS,
      reducedMotion,
      whitecapIntensity: whitecaps.intensity,
      windSpeedMps: conditions.windSpeedMps,
      surfaceYAt: this.water.surfaceYAt,
      foamMax,
      spindriftMax,
      particleMax,
    };

    this.foam.drawHullChurn({
      ...foamFrame,
      centreX: tuning.centreX,
      halfWidthPx: tuning.halfWidthPx,
      pitchRad: this.hull.pitchRad,
      heavePx: this.hull.heavePx,
      heaveRatePxS: this.hull.heaveRatePxS,
      pitchRateRadS: this.hull.pitchRateRadS,
      significantPx: this.sea.significantHeightPx(),
      judderChop: effectiveJudderChop,
    });

    this.foam.drawWhitecaps(foamFrame, this.water.surfacePoints());

    if (!reducedMotion) {
      this.hull.bowGapPx = this.foam.drawSpray({
        ...foamFrame,
        centreX: tuning.centreX,
        baseY,
        hullPx,
        pitchRad: this.hull.pitchRad,
        heavePx: this.hull.heavePx,
        capsize: this.hull.capsize,
        previousCapsize: this.hull.previousCapsize,
        previousGapPx: this.hull.bowGapPx,
        elevationAt: (x) => this.sea.elevationAt(x),
        clampPx,
      });

      this.hull.previousCapsize = this.hull.capsize;
    }

    this.waveScale.draw({
      width,
      height,
      baseY,
      significantHeightM,
      pxPerM,
      clampPx,
    });
  }

  /** Ease cloud, rain and storm toward this day's / hour's sky state. */
  private easeBackdrop(weatherCode: number | string, dt: number): void {
    const state = skyStateForWeatherCode(weatherCode);
    // Roughly a 1.5s glide; snap straight there under reduced motion.
    const ease = this.options.reducedMotion
      ? 1
      : Math.min(1, dt * SKY_EASE_RATE);

    this.cloudAmount += (CLOUD_TARGET[state] - this.cloudAmount) * ease;
    this.rainAmount += (RAIN_TARGET[state] - this.rainAmount) * ease;
    this.stormAmount += (STORM_TARGET[state] - this.stormAmount) * ease;
  }
}
